import React from 'react';
import { flexRender } from '@tanstack/react-table';

/* eslint-disable react/prop-types */
/**
 * @template {import('@tanstack/react-table').RowData} TData
 * @param {{ table: import('@tanstack/react-table').Table<TData>, loading: boolean, error: import('react').ReactNode, emptyMessage: import('react').ReactNode }} props
 */
const DataTableBody = ({ table, loading, error, emptyMessage, onRowClick }) => {
  const columnCount = table.getVisibleLeafColumns().length;
  const rows = table.getRowModel().rows;

  // Skeleton rows only on the very first load (no rows yet). Once there
  // is data on screen, a refetch (e.g. typing in a header filter) just
  // dims the existing rows instead of tearing them out for a skeleton -
  // swapping the whole body out on every keystroke is what made filtering
  // feel like a full page reload.
  if (loading && !rows.length) {
    return (
      <tbody aria-label="Loading records">
        {Array.from({ length: Math.min(table.getState().pagination.pageSize, 5) }, (_, rowIndex) => (
          <tr key={`loading-${rowIndex}`} className="border-b border-gray-100 last:border-0">
            {Array.from({ length: columnCount }, (_, cellIndex) => (
              <td key={`loading-${rowIndex}-${cellIndex}`} className="px-4 py-3">
                <div className="h-4 animate-pulse rounded bg-gray-100" />
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    );
  }

  if (error) {
    return (
      <tbody>
        <tr>
          <td colSpan={columnCount} className="px-4 py-10 text-center text-sm text-red-600" role="alert">
            {error}
          </td>
        </tr>
      </tbody>
    );
  }

  if (!rows.length) {
    return (
      <tbody>
        <tr>
          <td colSpan={columnCount} className="px-4 py-10 text-center text-sm text-gray-500">
            {emptyMessage}
          </td>
        </tr>
      </tbody>
    );
  }

  return (
    <tbody
      aria-busy={loading}
      className={`divide-y divide-gray-100 transition-opacity ${loading ? "pointer-events-none opacity-50" : ""}`}
    >
      {rows.map((row) => (
        <tr
          key={row.id}
          data-state={row.getIsSelected() ? "selected" : undefined}
          onClick={onRowClick ? () => onRowClick(row.original) : undefined}
          className={`hover:bg-gray-50/70 ${onRowClick ? "cursor-pointer" : ""}`}
        >
          {row.getVisibleCells().map((cell) => (
            <td
              key={cell.id}
              className="px-4 py-3 align-middle text-black [&_*:not([class*='text-white']):not(button):not(button_*)]:!text-black"
            >
              <div className="block max-w-[300px] break-words">
                {flexRender(cell.column.columnDef.cell, cell.getContext())}
              </div>
            </td>
          ))}
        </tr>
      ))}
    </tbody>
  );
};

export default DataTableBody;
